import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { readConsent, writeConsent } from '@/lib/analytics/consent';
import type { ConsentValue } from '@/lib/analytics/consent';
import { useCookieGeolocationConsent } from '@/features/collection-place';
import { translations } from '@/translations/translations';

export function CookieConsent() {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';
  const fr = currentLang === 'fr';
  const copy = translations[fr ? 'fr' : 'en'];
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const saveLocationConsent = useCookieGeolocationConsent();

  useEffect(() => {
    if (readConsent() === null) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const dismiss = (value: ConsentValue) => {
    writeConsent(value);
    void saveLocationConsent(value === 'accepted' ? 'granted' : 'denied')
      .catch(() => undefined);
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 220);
  };

  return (
    <>
      {/* Mobile-only backdrop — click-outside to reject */}
      <div
        className={`sm:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${closing ? 'opacity-0' : 'opacity-100'}`}
        onClick={() => dismiss('rejected')}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-label={copy.cookieConsentTitle}
        className={`fixed bottom-0 left-0 right-0 z-50 sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-md transition-all duration-300 ease-premium ${closing ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0 animate-rise-up'}`}
      >
        <div className="m-2 sm:m-0 sm:rounded-xl surface-2 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-full bg-yodi-orange/10 text-yodi-orange items-center justify-center">
              <Icon name="shield" className="w-5 h-5" />
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-foreground">
                {copy.cookieConsentTitle}
              </h2>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                {copy.cookieConsentDescription}{' '}
                <Link
                  to={`/${currentLang}/data-policy`}
                  onClick={() => dismiss('rejected')}
                  className="text-yodi-orange hover:underline whitespace-nowrap transition-colors"
                >
                  {copy.cookieConsentDataPolicy}
                </Link>
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => dismiss('accepted')}
                  className="interactive flex-1 min-w-0 h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 focus-ring"
                >
                  {copy.cookieConsentAcceptLocation}
                </button>
                <button
                  type="button"
                  onClick={() => dismiss('rejected')}
                  className="interactive flex-1 min-w-0 h-9 px-3 rounded-md border border-border/70 text-sm font-medium text-foreground hover:bg-muted/50 focus-ring"
                >
                  {copy.cookieConsentRejectLocation}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => dismiss('rejected')}
              aria-label={copy.cookieConsentClose}
              className="sm:hidden shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground"
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
